import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Lock, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ProductDetailDialog } from "@/components/ProductDetailDialog";
import { MultiPhotoUploader } from "@/components/MultiPhotoUploader";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";

const blank = { name: "", description: "", price: 0, taxable: true, active: true, image_data_urls: [] };

export default function Products() {
  const { user } = useAuth();
  const canEditPrices = user?.role === "admin";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [viewing, setViewing] = useState(null); // read-only product viewer
  const load = () => api.get("/products").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...blank, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (!form.price || form.price <= 0) return toast.error("Price must be > 0");
    try {
      if (editing) await api.put(`/products/${editing.id}`, form);
      else await api.post("/products", form);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted"); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="overline">Catalog</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Products</h1>
          <p className="text-sm text-muted-foreground mt-2">Pumps, heaters, parts and any add-on items billable on a ticket.</p>
        </div>
        {canEditPrices ? (
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-sm h-10" data-testid="add-product-button">
              <Plus className="w-4 h-4 mr-2" /> New product
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit product" : "New product"}</DialogTitle>
              <DialogDescription>Set a name and unit price. Mark whether sales tax applies.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" placeholder="e.g. Pentair IntelliFlo VSF Pump" /></div>
              <div><Label>Description</Label><Textarea className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="space-y-2">
                <MultiPhotoUploader value={form.image_data_urls || []} onChange={(images) => setForm({ ...form, image_data_urls: images })} label="Product photos" testId="product-photos" max={12} sharedAcrossColonies />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1">Unit price ($) {!canEditPrices && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
                  <Input type="number" step="0.01" className="rounded-sm mono-num" value={form.price}
                         onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })}
                         readOnly={!canEditPrices} disabled={!canEditPrices}
                         data-testid="product-price-input" />
                  {!canEditPrices && <p className="text-[10px] text-muted-foreground mt-1">Only the admin can change prices.</p>}
                </div>
                <div className="flex flex-col">
                  <Label>Taxable</Label>
                  <div className="h-9 flex items-center"><Switch checked={form.taxable} onCheckedChange={(v) => setForm({ ...form, taxable: v })} data-testid="product-taxable-switch" /></div>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active-switch" />
                <Label className="cursor-pointer">Active (available to add on tickets)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="rounded-sm" data-testid="save-product-button">{editing ? "Save changes" : "Create product"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        ) : (
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Admin-only</div>
        )}
      </div>

      <div className="border border-border overflow-x-auto">
        <div className="min-w-[700px]">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline">
          <div className="col-span-5">Name</div>
          <div className="col-span-3">Description</div>
          <div className="col-span-2">Price</div>
          <div className="col-span-1">Tax</div>
          <div className="col-span-1 text-right">·</div>
        </div>
        {list.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">No products yet — add a pool pump, heater or accessory to get started.</div>}
        {list.map((p) => (
          <div key={p.id} className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40" data-testid={`product-row-${p.id}`}>
            <button type="button" className="col-span-5 flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
                    onClick={() => setViewing(p)} data-testid={`view-product-${p.id}`}>
              {(p.image_data_urls || []).length > 0 ? (
                <div className="relative flex-shrink-0">
                  <LivePhotoSurface value={p.image_data_urls[0]} alt={p.name} className="w-12 h-12 rounded-sm border border-border" testId={`product-thumb-${p.id}`} />
                  {p.image_data_urls.length > 1 && (
                    <span className="absolute -bottom-1 -right-1 bg-foreground text-background text-[9px] font-bold rounded-sm px-1">+{p.image_data_urls.length - 1}</span>
                  )}
                </div>
              ) : (
                <div className="w-12 h-12 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground flex-shrink-0">
                  <Package className="w-4 h-4" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-medium underline-offset-2 hover:underline">{p.name}</div>
                {!p.active && <span className="text-xs text-muted-foreground">(inactive)</span>}
              </div>
            </button>
            <div className="col-span-3 text-sm text-muted-foreground truncate">{p.description || "—"}</div>
            <div className="col-span-2 mono-num text-sm">{fmtMoney(p.price)}</div>
            <div className="col-span-1 text-xs font-bold tracking-wider uppercase">{p.taxable ? <span className="text-primary">YES</span> : <span className="text-muted-foreground">NO</span>}</div>
            <div className="col-span-1 text-right flex justify-end gap-1">
              {canEditPrices ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="rounded-sm" data-testid={`edit-product-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} className="rounded-sm text-destructive hover:text-destructive" data-testid={`delete-product-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : (
                <Lock className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
      <ProductDetailDialog product={viewing} open={!!viewing} onOpenChange={(v) => !v && setViewing(null)} />
    </div>
  );
}
